import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function HeatTransferCalculator() {
  const [mass, setMass] = useState("");
  const [specificHeat, setSpecificHeat] = useState("4186");
  const [tempChange, setTempChange] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateHeat = () => {
    const m = parseFloat(mass);
    const c = parseFloat(specificHeat);
    const deltaT = parseFloat(tempChange);

    if (isNaN(m) || isNaN(c) || isNaN(deltaT)) {
      toast.error("Please enter valid values");
      return;
    }

    const heat = m * c * deltaT;
    setResult(heat);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMass("");
    setSpecificHeat("4186");
    setTempChange("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Heat Transfer Calculator" calculatorPath="/calculator/heat-transfer">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Heat Transfer Calculator</h1>
          <p className="text-muted-foreground">
            Calculate heat energy transferred
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: Q = mcΔT</CardTitle>
            <CardDescription>
              Where Q is heat (J), m is mass (kg), c is specific heat (J/kg·°C), and ΔT is temperature change (°C)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mass">Mass (kg)</Label>
                <Input
                  id="mass"
                  type="number"
                  step="0.01"
                  placeholder="Enter mass"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="specificHeat">Specific Heat Capacity (J/kg·°C)</Label>
                <Input
                  id="specificHeat"
                  type="number"
                  step="0.01"
                  placeholder="Water: 4186 J/kg·°C"
                  value={specificHeat}
                  onChange={(e) => setSpecificHeat(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Water: 4186, Ice: 2100, Aluminum: 900, Iron: 450</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tempChange">Temperature Change (°C)</Label>
                <Input
                  id="tempChange"
                  type="number"
                  step="0.01"
                  placeholder="Enter temperature change"
                  value={tempChange}
                  onChange={(e) => setTempChange(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateHeat} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} J
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Heat Energy ({(result / 1000).toFixed(2)} kJ)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["heat-transfer"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the mass of the substance in kilograms</p>
                <p>2. Enter specific heat capacity (default is water)</p>
                <p>3. Enter the temperature change</p>
                <p>4. Click Calculate to get heat energy transferred</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 1 kg water heated by 10°C = 41,860 J
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
